import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { BookmarkPlus, Trash2 } from "lucide-react";
import { ReviewVideoFullscreenPlayer } from "../../components/ReviewVideoFullscreenPlayer";
import {
  isAlreadySignedMediaUrl,
  needsSignedMediaUrl,
  peekSignedMediaUrl,
  prefetchSignedMediaUrls,
  rememberSignedMediaUrl,
  resolveSignedMediaUrl,
} from "../../api/signedMediaUrl";
import "./LiveTrainingTableReplayReview.css";

/**
 * Same portal target as ReviewVideoFullscreenPlayer — Save / Discard must sit
 * in that stacking context or they end up under the black overlay.
 */
function getReplayReviewPortalRoot() {
  if (typeof document === "undefined") return null;
  const fs =
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    null;
  if (fs?.classList?.contains("lt-page")) return fs;
  return document.body;
}

/**
 * Opens the same full-screen review player used for evaluation videos.
 * Multiple camera clips from a table request are queued — Save / Discard /
 * Close advances to the next clip.
 */
export function LiveTrainingTableReplayReview({
  clips = [],
  busy = false,
  onClose,
  onSave,
  onDelete,
  teId = null,
  brandPrimary = "#0877BC",
  brandSecondary = "#46528b",
  t = (k, d) => d || k,
}) {
  const list = useMemo(
    () => (Array.isArray(clips) ? clips.filter((c) => c?.message_id) : []),
    [clips]
  );
  const clip = list[0] || null;
  const clipsRemaining = Math.max(0, list.length - 1);
  const rawUrl = clip?.playback_url || clip?.video_message_url || "";
  const [playUrl, setPlayUrl] = useState(
    () => peekSignedMediaUrl(rawUrl) || rawUrl
  );

  useEffect(() => {
    const unsigned = [];
    for (const c of list) {
      if (isAlreadySignedMediaUrl(c?.playback_url)) {
        rememberSignedMediaUrl(c.playback_url);
      } else if (c?.video_message_url || c?.playback_url) {
        unsigned.push(c.video_message_url || c.playback_url);
      }
    }
    prefetchSignedMediaUrls(unsigned);
  }, [list]);

  useEffect(() => {
    let cancelled = false;
    // The server already signed playback_url (S3, or the backend clip route
    // for unsaved replays). Re-signing would swap the <video> src and restart
    // the clip, or replace it with the unsigned backend URL.
    if (isAlreadySignedMediaUrl(rawUrl) || !needsSignedMediaUrl(rawUrl)) {
      rememberSignedMediaUrl(rawUrl);
      setPlayUrl(rawUrl);
      return undefined;
    }
    const cached = peekSignedMediaUrl(rawUrl);
    setPlayUrl(cached || rawUrl);
    if (!rawUrl || cached) return undefined;
    resolveSignedMediaUrl(clip?.video_message_url || rawUrl)
      .then((signed) => {
        if (!cancelled && signed) setPlayUrl(signed);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [rawUrl, clip?.video_message_url, clip?.message_id]);

  if (!clip || !playUrl || typeof document === "undefined") return null;

  const tableHint =
    clip.table_number != null
      ? t("table_with_number", { number: clip.table_number })
      : t("liveTrainingReplayTitle", "Last 10 seconds");
  const title =
    list.length > 1
      ? t("liveTrainingReplayCameraIndex", "Camera {{index}} of {{total}}", {
          index: 1,
          total: list.length,
        })
      : t("liveTrainingReplayTitle", "Last 10 seconds");

  const portalRoot = getReplayReviewPortalRoot() || document.body;

  return (
    <>
      <ReviewVideoFullscreenPlayer
        key={clip.message_id || playUrl}
        url={playUrl}
        onClose={onClose}
        title={title}
        autoPlay
        eagerPlayback
        enablePose={false}
        enableJointOverlay
        jointOverlayAllPlayers
        isEvaluationVideo={false}
        messageId={clip.message_id || null}
        teId={clip.te_id || teId || null}
        className="review-video-fullscreen-player--table-replay"
      />
      {createPortal(
        <div
          className="lt-replay-review-actions"
          role="toolbar"
          aria-label={tableHint}
          style={{
            "--lt-brand": brandPrimary,
            "--lt-brand-secondary": brandSecondary,
          }}
        >
          {clipsRemaining > 0 ? (
            <span
              className="lt-replay-review-actions__queue"
              title={t(
                "liveTrainingReplayQueue",
                "{{count}} more from this table",
                { count: clipsRemaining }
              )}
            >
              +{clipsRemaining}
            </span>
          ) : null}
          <button
            type="button"
            className="lt-replay-review-actions__btn lt-replay-review-actions__btn--discard"
            disabled={busy}
            onClick={() => onDelete?.(clip.message_id)}
            aria-label={t("liveTrainingReplayDelete", "Discard")}
          >
            <Trash2 size={18} strokeWidth={2.25} aria-hidden />
            <span>{t("liveTrainingReplayDelete", "Discard")}</span>
          </button>
          <button
            type="button"
            className="lt-replay-review-actions__btn lt-replay-review-actions__btn--save"
            disabled={busy}
            onClick={() => onSave?.(clip.message_id)}
            aria-label={t("liveTrainingReplaySave", "Save feedback")}
          >
            <BookmarkPlus size={18} strokeWidth={2.25} aria-hidden />
            <span>{t("liveTrainingReplaySave", "Save feedback")}</span>
          </button>
        </div>,
        portalRoot
      )}
    </>
  );
}

export default LiveTrainingTableReplayReview;
